<template>
  <with-profile-tabs
      show-cards
      :available-tabs="['personal-home', 'preferences', 'notification-settings', 'password-and-security']"
      :title="$t('profile_settings_title')"
      @updatedProfilePicture="resetUserObjects()"
  >
    <div class="profile fixed-heading">
      <notification-settings
          v-if="isNotificationTab"
          @settings-updated="resetUserObjects"
      />


      <div
          class="scrollable profile-settings-panel"
          v-else-if="$route.hash === '#password-and-security' && isPersonalHomeAvailable"
      >
        <form
            class="profile__form"
            @submit.prevent="saveUpdatedPhoneNumber"
        >
          <h2 class="h2">{{ $t('profile_phone_number_title') }}</h2>
          <div class="row">
            <form-group
                is-simplified
                :label="$t('profile_phone')"
                class="col-xs-12"
            >
              <ds-input
                  is-simplified
                  placeholder="+32 477 280 200."
                  type="tel"
                  v-model="updatedEmailUserObject.phone"
              />
              <span v-if="newPhoneNumberMessage">{{ newPhoneNumberMessage }}</span>
              <Checkbox
                  style="margin-top: 1rem;"
                  label="Share your phone number with other users"
                  :model-value="showPhoneNumber"
                  @update:model-value="updateShowPhoneNumber($event)"
              />
            </form-group>

            <div
                class="col-xs-12"
                style="margin-bottom: 20px"
            >
              <ds-button
                  :label="$t('profile_phone_number_save')"
                  variant="rounded"
                  :disabled="!isPhoneNumberUnchanged"
                  @click="saveUpdatedPhoneNumber"
              />
            </div>
          </div>
        </form>
        <form
            class="profile__form"
            @submit.prevent="saveUpdatedPassword"
        >
          <h2 class="h2">{{ $t('profile_password_title') }}</h2>
          <div class="row">
            <form-group
                is-simplified
                :label="$t('profile_password_current_label')"
                :errors="errors.current_password"
                class="col-xs-12"
            >
              <ds-input
                  is-simplified
                  type="password"
                  v-model="updatedPasswordUserObject.current_password"
              />
            </form-group>

            <form-group
                is-simplified
                :label="$t('profile_password_new_label')"
                :errors="errors.password"
                class="col-xs-12 col-sm-6"
            >
              <ds-input
                  is-simplified
                  type="password"
                  v-model="updatedPasswordUserObject.password"
                  :noAutocomplete="true"
              />
            </form-group>

            <form-group
                is-simplified
                :label="$t('profile_password_confirm_label')"
                :errors="errors.password_confirmation"
                class="col-xs-12 col-sm-6"
            >
              <ds-input
                  is-simplified
                  type="password"
                  v-model="updatedPasswordUserObject.password_confirmation"
              />
            </form-group>
            <div
                class="col-xs-12"
                style="margin-bottom: 20px"
            >
              <ds-button
                  :label="$t('profile_edit_password_save')"
                  variant="rounded"
                  :disabled="!isEditingPassword"
                  @click="saveUpdatedPassword"
              />
            </div>
          </div>
        </form>
        <form
            class="profile__form"
            @submit.prevent="saveUpdatedEmailAddress"
        >
          <h2 class="h2">{{ $t('profile_email_title') }}</h2>
          <div class="row">
            <form-group
                is-simplified
                :label="$t('profile_email_label')"
                :errors="errors.email"
                class="col-xs-12"
            >
              <ds-input
                  is-simplified
                  v-model="updatedEmailUserObject.email"
                  disabled
              />
            </form-group>

            <form-group
                is-simplified
                :label="$t('profile_update_email_label')"
                :errors="newEmailErrors.newEmail"
                class="col-xs-12"
            >
              <ds-input
                  is-simplified
                  v-model="newEmail"
              />
              <span v-if="newEmailMessage">{{ newEmailMessage }}</span>
            </form-group>
            <div
                class="col-xs-12"
                style="margin-bottom: 20px"
            >
              <ds-button
                  :label="$t('profile_edit_email_save')"
                  variant="rounded"
                  :disabled="!isEditingEmail || !validNewEmail"
                  @click="saveUpdatedEmailAddress"
              />
            </div>
          </div>
        </form>

        <form
            class="profile__form"
            style="margin-bottom: 1rem;"
        >
          <h2 class="h2">{{ $t('user_profile_leave_ecosystem') }}</h2>
          <div class="row">
            <div class="col-xs-12">
              <ds-button
                  variant="rounded"
                  size="small"
                  icon="trash"
                  @click="displayLeaveEcosystem"
                  :label="$t('user_profile_leave_ecosystem_button_label')"
              />
            </div>
          </div>
        </form>
      </div>
      <div
          class="scrollable personal-home-panel"
          v-else-if="!isPersonalHomeDisabled"
      >
        <personal-home/>
      </div>
    </div>
  </with-profile-tabs>
</template>

<script>
import Dropdown from '../Dropdown/Dropdown.vue'
import Checkbox from '../Form/Checkbox.vue'

import { acceptTerms, changeEmailRequest, fetchActorProfiles, updatePhoneNumber, updateProfile } from '../../api/user'

import isEqual from 'lodash/isEqual'

import MODAL_IDS from '../../constants/modal-ids'
import { humanize } from '../../constants/properties'
import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui'
import { prepareUserForEditing } from '../../util/user-settings'
import { LOCALE_LABELS } from '../../store/modules/localization'
import { prepareConfigForEditing } from '../../util/config.js'
import ConceptSearchMixin from '../../util/ConceptSearchMixin'
import DsTextarea from '../Form/DsTextarea.vue'
import ToggleButton from '../Form/ToggleButton.vue'
import SimpleCard from '../SimpleCard/SimpleCard.vue'
import { trackHeapEvent } from '../../util/analytics.js'
import { ACTION_TYPES as ACTORS_ACTION_TYPES } from '../../store/modules/actors.js'
import HoverOverlayButton from '../Overlay/HoverOverlayButton.vue'
import TranslationsMixin from '../../util/TranslationsMixin.js'
import Modal from '../Modals/Modal.vue'
import { DEFAULT_URLS } from '../../constants/default-urls.js'
import { SiteTemplate } from '../../store/modules/config.js'
import WithProfileTabs from '../../pages/WithConfigurationTabs/WithProfileTabs.vue'
import { MUTATION_TYPES as USER_MUTATION_TYPES } from '../../store/modules/user-helpers.js'
import PersonalHome from './PersonalHome.vue'
import CommunityMixin from '@/util/CommunityMixin'
import NotificationSettings from './NotificationSettings.vue'

export default {
  data () {
    return {
      errors: {},
      user: this.currentUser(),
      profiles: [],
      originalLocale: this.$store.getters.activeLocale,
      config: prepareConfigForEditing(this.$store.state.config),
      // separate user objects to prevent saving other data when pressing a specific save button
      updatedPasswordUserObject: this.currentUser(),
      updatedEmailUserObject: this.currentUser(),
      errorMessage: null,
      newEmail: null,
      newEmailMessage: null,
      newEmailErrors: {},
      newPhoneNumber: null,
      newPhoneNumberMessage: null,
      initialPhoneNumber: '',
    }
  },
  mixins: [ConceptSearchMixin, TranslationsMixin, CommunityMixin],
  watch: {
  },
  computed: {
    showPhoneNumber () {
      return this.user.phone_published === 1;
    },
    isPhoneNumberUnchanged () {
      return this.updatedEmailUserObject.phone !== this.initialPhoneNumber
    },
    isMember () {
      return this.$store.getters.isMember
    },
    isSimplifiedTemplateUsed () {
      return [SiteTemplate.NEW_SIMPLIFIED, SiteTemplate.SIMPLIFIED].includes(this.$store.getters.activeSiteTemplate)
    },
    isPersonalHomeDisabled () {
      return !!this.$store.state.config.personalHomeDisabled
    },
    isPersonalHomeAvailable () {
      return true
    },
    isEditingPassword () {
      return !isEqual(this.updatedPasswordUserObject, this.currentUser())
    },
    isEditingEmail () {
      return !isEqual(this.updatedEmailUserObject, this.currentUser()) || this.newEmail
    },
    validNewEmail () {
      if (!this.newEmail || this.newEmail === this.updatedEmailUserObject.email) {
        return false
      }

      const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(this.newEmail).toLowerCase())
    },
    canOnlyCreateProducts () {
      return this.$store.getters.claimableActorTypes.includes('Product') && this.$store.getters.claimableActorTypes.length === 1
    },
    isNotificationTab () {
      return this.$route.hash === '#notification-settings' || (!this.$route.hash && this.isPersonalHomeDisabled)
    },
    userCanCreate () {
      return this.$store.getters.userCanCreate
    },
    profile () {
      return this.$store.state.user.profile
    },
    enabledLocales () {
      return this.$store.state.localization.enabledLocales
    },
    ecosystemDisplayName () {
      return this.$store.getters.ecosystemDisplayName
    },
    termsOfUseUrl () {
      return this.config.termsOfUseUrl || DEFAULT_URLS.TERMS_OF_USE
    },
    privacyPolicyUrl () {
      return this.config.privacyPolicyUrl || DEFAULT_URLS.PRIVACY_POLICY
    },
    hasAccessToMonitoring () {
      return this.$store.getters.hasAccessToMonitoring
    },
    hasAccessToPublisher () {
      return this.$store.getters.hasAccessToPublisher
    },
    isFollowingSearches () {
      return this.followedConceptSearchIds.length > 0
    },
    isOwner () {
      return this.$store.getters.isOwner
    },
  },
  methods: {
    updateShowPhoneNumber (newValue) {
      updatePhoneNumber({ phone: this.user.phone, phone_published: newValue ? 1 : 0 })
          .then(data => {
            trackHeapEvent('updatePhoneNumber.success')
          })
          .catch((errors) => {
            console.log(errors)
          })
    },
    displayLeaveEcosystem () {
      this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.USER_LEAVE_ECOSYSTEM)
    },
    userIsActorOwner (actor) {
      if (this.$store.getters.isActor) {
        const claim = this.$store.state.user.profile.claims.find(claim => claim.id === actor.id) || {}
        return claim.claim_type === 'actor-owner' && claim.claim_verified
      }
      return false
    },
    openCreateActorModal () {
      this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR)
    },
    handleClickActorCard (actor) {
      this.$store.dispatch(ACTORS_ACTION_TYPES.FETCH_ACTOR_FOR_PREVIEW, actor)
      trackHeapEvent('userProfile.claims.clickActorCard')

      window.location.href = '/actors/' + actor.id
    },
    humanize,
    labelForLocale (locale) {
      return LOCALE_LABELS[locale]
    },
    acceptTerms () {
      acceptTerms().then(response => {
        this.$store.commit('USER/UPDATE_PROFILE', { accepted_terms: response.accepted_terms })
      })
          .catch(error => {
          })
    },
    currentUser () {
      const profile = this.$store.state.user.profile
      return prepareUserForEditing(profile)
    },
    saveUpdatedPhoneNumber () {
      updatePhoneNumber({ phone: this.updatedEmailUserObject.phone, phone_published: true })
          .then(response => {
            this.newPhoneNumberMessage = 'Your phone number is successfully updated'
            trackHeapEvent('saveUpdatedPhoneNumber.success')
          })
          .catch(errors => {
            console.log(errors)
          })
    },
    saveUpdatedEmailAddress () {
      if (!this.isEditingEmail || !this.validNewEmail) {
        return
      }
      this.newEmailErrors = {}
      this.newEmailMessage = ''

      changeEmailRequest(this.newEmail)
          .then(response => {
            this.newEmailMessage = 'An email has been sent to ' + this.newEmail + ' in order to complete the update.'
            this.newEmail = ''
          })
          .catch(errors => {
            this.newEmailErrors = errors
          })
    },
    resetUserObjects () {
      var currentUser = this.currentUser()

      this.user = { ...currentUser }
      this.updatedPasswordUserObject = { ...currentUser }
      this.updatedEmailUserObject = { ...currentUser }
    },
    async saveUpdatedPassword () {
      if (!this.isEditingPassword) {
        return
      }

      updateProfile({
        current_password: this.updatedPasswordUserObject.current_password,
        password: this.updatedPasswordUserObject.password,
        password_confirmation: this.updatedPasswordUserObject.password_confirmation,
      })
          .then(data => {
            this.errors = {}
            this.$store.commit(USER_MUTATION_TYPES.UPDATE_PROFILE, data)
            setTimeout(() => {
              this.resetUserObjects()
            })
            trackHeapEvent('changePassword.success')
          })
          .catch(errors => {
            this.errors = errors
            trackHeapEvent('changePassword.failure', { errorMessage: JSON.stringify(errors) })
          })
    },
    redirectLink (url) {
      var link = ''
      // If the user has a claimed actor, the user is redirected to the actors page
      // Else the user is redirected to the ecosystem home page
      if (this.profiles.length > 0) {
        this.profiles.forEach(profile => {
          if (profile.url == url) {
            link = profile.url + '/actors/' + profile.id
          } else {
            link = url
          }
        })
      } else {
        link = url
      }
      return link
    },
    hidePanel () {
      this.$store.commit(UI_MUTATION_TYPES.HIDE_SIDE_PANEL)
    },
  },
  mounted () {
    if (this.$route.hash === '#personal-home' && this.$store.state.config.personalHomeDisabled) {
      this.$router.push('/profile/update#notification-settings')
    }

    this.initialPhoneNumber = this.updatedEmailUserObject.phone

    fetchActorProfiles()
        .then(profiles => {
          this.profiles = profiles
        })
        .catch()


    this.resetUserObjects()

    this.$bus.on('updateUserProfile', this.resetUserObjects)
    
  },
  beforeUnmount () {
    this.$bus.off('updateUserProfile', this.resetUserObjects)
  },
  components: {
    WithProfileTabs,
    Checkbox,
    Dropdown,
    DsTextarea,
    ToggleButton,
    SimpleCard,
    HoverOverlayButton,
    Modal,
    PersonalHome,
    NotificationSettings,
  },
}
</script>

<style
    lang="scss"
    scoped
>
@import "../../../scss/_variables.scss";

:deep(.form-group) {
  .form-group__label__text {
    color: var(--primary);
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.profile__form {
  margin-left: 0;
  max-width: 700px;

  @media (min-width: $screen-lg) and (max-width: $screen-xl) {
    max-width: 600px;
  }
  @media (max-width: $screen-lg) {
    margin: 0 auto;
  }

  h2.h2 {
    font-size: 18px;
    padding-bottom: 10px;
    color: var(--primary);
    border-bottom: 2px solid var(--primary-extra-lightest);
  }

  .button:disabled {
    background-color: $color-disabled;
    color: white;
  }
}

:deep(.action-bar) {
  position: fixed;

  &.action-bar-with-navigation {
    left: 39px;
  }
}

.notification-settings-panel, .profile-settings-panel, .personal-home-panel {
  @media (min-width: $screen-lg) {
    padding-left: 0;
    padding-right: 0;
  }

  .notification-settings-panel__settings-table {
    border-collapse: collapse;
    margin: 0 auto;
    @media (min-width: $screen-lg) {
      margin-left: 0;
      max-width: 700px;
    }
    @media (min-width: $screen-lg) and (max-width: $screen-xl) {
      max-width: 600px;
    }

    th {
      border-bottom: 1px solid var(--primary-lightest);
      color: var(--primary);
    }

    td.label-column, td.website-column, td.label-column, td.slack-column td.email-column {
      padding-bottom: 5px;
      padding-top: 5px;
    }
  }
}

.notification-settings-panel {
  @media (max-width: $screen-lg) {
    max-width: 590px;
    margin: 0 auto;
  }
}

.profile-settings-panel {
  @media (max-width: $screen-lg) {
    margin: 0 auto;
    max-width: 590px;
  }
}

.personal-home-panel {
  //allow some space for the box-shadow
  margin: -10px;
  padding: 20px 0;
  @media (max-width: $screen-md) {
    padding: 0 20px 20px 20px;
  }
}

:deep(.multiselect.multiselect--datascouts) {
  .multiselect__tags {
    min-height: 28px;
    margin-top: 2px;

    .multiselect__single {
      height: 18px;
      padding-top: 1px;
    }
  }
}
</style>
